import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminAPI, cityAPI } from '../../services/api';
import Navbar from '../../components/Navbar';
import './Notifications.css';

const Notifications = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [uploading, setUploading] = useState(''); // '' | 'broadcast' | 'individual'
  const [exportFilter, setExportFilter] = useState('reachable'); // reachable | unreachable | all
  const [exporting, setExporting] = useState(false);

  // Download customers (name + phone) as an Excel file, scoped by push reachability.
  const handleExportCustomers = async () => {
    setExporting(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await adminAPI.exportNotificationCustomers(exportFilter);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const dispo = res.headers?.['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(dispo);
      const day = new Date().toISOString().slice(0, 10);
      const fname = m ? m[1] : `foodwinc-customers-${exportFilter}-${day}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to export customers' });
    } finally {
      setExporting(false);
    }
  };

  // Upload an image file to R2 and drop the returned public URL into the form.
  const uploadImage = async (file, which) => {
    if (!file) return;
    setUploading(which);
    setMessage({ type: '', text: '' });
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await adminAPI.uploadNotificationImage(fd);
      const url = res.data?.url;
      if (!url) throw new Error('No URL returned');
      if (which === 'broadcast') setBroadcastForm((p) => ({ ...p, imageUrl: url }));
      else setIndividualForm((p) => ({ ...p, imageUrl: url }));
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Image upload failed' });
    } finally {
      setUploading('');
    }
  };

  // Active cities for the location filters
  const [cities, setCities] = useState([]);

  // Broadcast notification form
  const [broadcastForm, setBroadcastForm] = useState({
    targetAudience: 'all',
    city: '',
    title: '',
    body: '',
    imageUrl: '',
    data: {}
  });

  // Live recipient count for the current broadcast targeting (audience + city)
  const [broadcastCount, setBroadcastCount] = useState({ total: 0, withToken: 0, loading: false });

  // Individual notification: searchable/filterable recipient picker
  const [picker, setPicker] = useState({ role: 'all', city: '', search: '' });
  const [recipients, setRecipients] = useState([]);
  const [recipMeta, setRecipMeta] = useState({ total: 0, withTokenCount: 0, page: 1, totalPages: 1, loading: false });
  const [selectedUser, setSelectedUser] = useState(null);

  const [individualForm, setIndividualForm] = useState({
    title: '',
    body: '',
    imageUrl: '',
    data: {}
  });

  const [activeTab, setActiveTab] = useState('broadcast');

  // ---- WhatsApp broadcast (MSG91) ----
  const [whatsappForm, setWhatsappForm] = useState({ templateName: '', languageCode: 'en', audience: 'no_token', city: '', useName: false, bodyVars: '', testPhone: '' });
  const [waCount, setWaCount] = useState({ total: 0, withPhone: 0, configured: false, loading: false });
  const [waSending, setWaSending] = useState(false);

  // Live audience count for the WhatsApp tab (audience + city)
  useEffect(() => {
    if (activeTab !== 'whatsapp') return undefined;
    let cancelled = false;
    (async () => {
      setWaCount((c) => ({ ...c, loading: true }));
      try {
        const { data } = await adminAPI.getWhatsAppCount({ audience: whatsappForm.audience, city: whatsappForm.city || undefined });
        if (!cancelled && data.success) setWaCount({ ...data.data, loading: false });
      } catch (e) { if (!cancelled) setWaCount((c) => ({ ...c, loading: false })); }
    })();
    return () => { cancelled = true; };
  }, [activeTab, whatsappForm.audience, whatsappForm.city]);

  const handleWhatsAppSend = async (testOnly) => {
    if (!whatsappForm.templateName.trim()) { setMessage({ type: 'error', text: 'Template name is required' }); return; }
    if (testOnly && !whatsappForm.testPhone.trim()) { setMessage({ type: 'error', text: 'Enter a test phone number' }); return; }
    if (!testOnly && !window.confirm(`Send WhatsApp template "${whatsappForm.templateName}" to ${waCount.withPhone} user(s)?`)) return;
    setWaSending(true); setMessage({ type: '', text: '' });
    try {
      const bodyVars = whatsappForm.bodyVars.split('|').map((s) => s.trim()).filter(Boolean);
      const { data } = await adminAPI.sendWhatsAppBroadcast({
        templateName: whatsappForm.templateName.trim(),
        languageCode: whatsappForm.languageCode || 'en',
        audience: whatsappForm.audience,
        city: whatsappForm.city || undefined,
        useName: whatsappForm.useName,
        bodyVars,
        testPhone: testOnly ? whatsappForm.testPhone.trim() : undefined,
      });
      if (data.success) setMessage({ type: 'success', text: data.test ? `Test accepted (configured=${data.data?.configured}). Check the test phone.` : (data.message || 'Sent') });
      else setMessage({ type: 'error', text: data.message || 'Failed' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'WhatsApp send failed' });
    }
    setWaSending(false);
  };

  // Token freshness (#3): prune per-role FCM tokens not refreshed in 270 days.
  const handlePruneStale = async () => {
    if (!window.confirm('Prune per-role FCM tokens not refreshed in 270 days?\n\n(These are expired per FCM and undeliverable. Users re-register automatically on next app open.)')) return;
    setMessage({ type: '', text: '' });
    try {
      const { data } = await adminAPI.pruneStaleTokens({ days: 270 });
      setMessage({ type: data.success ? 'success' : 'error', text: data.message || 'Done' });
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Prune failed' });
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCities();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getNotificationStats();
      setStats(response.data.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch notification stats' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await cityAPI.getActiveCities();
      setCities(response.data?.data?.cities || []);
    } catch (error) {
      // Non-fatal: location filter just won't be available
      setCities([]);
    }
  };

  // ── Live recipient count for the broadcast tab (audience + city) ───────────
  useEffect(() => {
    if (activeTab !== 'broadcast') return;
    let cancelled = false;
    const run = async () => {
      setBroadcastCount((c) => ({ ...c, loading: true }));
      try {
        const res = await adminAPI.getNotificationRecipients({
          role: broadcastForm.targetAudience,
          city: broadcastForm.city,
          limit: 1
        });
        if (cancelled) return;
        const d = res.data.data;
        setBroadcastCount({ total: d.total, withToken: d.withTokenCount, loading: false });
      } catch (e) {
        if (!cancelled) setBroadcastCount({ total: 0, withToken: 0, loading: false });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, broadcastForm.targetAudience, broadcastForm.city]);

  // ── Debounced recipient search for the individual tab ──────────────────────
  const fetchRecipients = useCallback(async (pageNum = 1, append = false) => {
    setRecipMeta((m) => ({ ...m, loading: true }));
    try {
      const res = await adminAPI.getNotificationRecipients({
        role: picker.role,
        city: picker.city,
        search: picker.search.trim(),
        page: pageNum,
        limit: 25
      });
      const d = res.data.data;
      setRecipients((prev) => (append ? [...prev, ...d.recipients] : d.recipients));
      setRecipMeta({
        total: d.total,
        withTokenCount: d.withTokenCount,
        page: d.page,
        totalPages: d.totalPages,
        loading: false
      });
    } catch (e) {
      setRecipMeta((m) => ({ ...m, loading: false }));
      setRecipients([]);
    }
  }, [picker.role, picker.city, picker.search]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (activeTab !== 'individual') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRecipients(1, false), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [activeTab, fetchRecipients]);

  const handleBroadcastSubmit = async (e) => {
    e.preventDefault();

    if (!broadcastForm.title.trim() || !broadcastForm.body.trim()) {
      setMessage({ type: 'error', text: 'Title and body are required' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await adminAPI.sendBroadcastNotification({
        targetAudience: broadcastForm.targetAudience,
        city: broadcastForm.city || undefined,
        title: broadcastForm.title.trim(),
        body: broadcastForm.body.trim(),
        imageUrl: (broadcastForm.imageUrl || '').trim() || undefined,
        data: broadcastForm.data
      });

      const { successCount, failureCount } = response.data.data;
      const where = broadcastForm.city ? ` in ${broadcastForm.city}` : '';
      setMessage({
        type: 'success',
        text: `Notification sent${where}! ${successCount} delivered, ${failureCount} failed.`
      });

      // Reset message fields, keep targeting so admin can send another
      setBroadcastForm((f) => ({ ...f, title: '', body: '', data: {} }));
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send notification'
      });
    } finally {
      setSending(false);
    }
  };

  const handleIndividualSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      setMessage({ type: 'error', text: 'Select a recipient first' });
      return;
    }
    if (!individualForm.title.trim() || !individualForm.body.trim()) {
      setMessage({ type: 'error', text: 'Title and body are required' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      await adminAPI.sendNotificationToUser({
        imageUrl: (individualForm.imageUrl || '').trim() || undefined,
        userId: selectedUser._id,
        title: individualForm.title.trim(),
        body: individualForm.body.trim(),
        data: individualForm.data
      });

      setMessage({ type: 'success', text: `Notification sent to ${selectedUser.name || 'user'}!` });
      setIndividualForm({ title: '', body: '', imageUrl: '', data: {} });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send notification to user'
      });
    } finally {
      setSending(false);
    }
  };

  const getAudienceLabel = (audience) => {
    switch (audience) {
      case 'all': return 'All Users';
      case 'users': return 'Customers Only';
      case 'restaurants': return 'Restaurants Only';
      case 'delivery': return 'Delivery Partners Only';
      default: return audience;
    }
  };

  const roleBadge = (role) => {
    switch (role) {
      case 'restaurant': return { label: 'Restaurant', cls: 'role-restaurant' };
      case 'delivery': return { label: 'Delivery', cls: 'role-delivery' };
      default: return { label: 'Customer', cls: 'role-user' };
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <Navbar />
        <div className="admin-content">
          <div className="loading">Loading notification stats...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Navbar />
      <div className="admin-content">
        <div className="admin-header">
          <h1>Push Notifications</h1>
          <p>Send push notifications to users, restaurants, and delivery partners</p>
        </div>

        <div className="notifications-container">
          {/* Stats Card */}
          <div className="notifications-card stats-card">
            <h2>Notification Stats</h2>
            <p className="card-description">Users with FCM tokens registered for push notifications</p>

            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-icon users-icon">👥</div>
                <div className="stat-info">
                  <div className="stat-label">Customers</div>
                  <div className="stat-value">{stats?.users?.withToken || 0}</div>
                  <div className="stat-total">of {stats?.users?.total || 0} total</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon restaurant-icon">🍽️</div>
                <div className="stat-info">
                  <div className="stat-label">Restaurants</div>
                  <div className="stat-value">{stats?.restaurants?.withToken || 0}</div>
                  <div className="stat-total">of {stats?.restaurants?.total || 0} total</div>
                </div>
              </div>

              <div className="stat-item">
                <div className="stat-icon delivery-icon">🚴</div>
                <div className="stat-info">
                  <div className="stat-label">Delivery Partners</div>
                  <div className="stat-value">{stats?.delivery?.withToken || 0}</div>
                  <div className="stat-total">of {stats?.delivery?.total || 0} total</div>
                </div>
              </div>

              <div className="stat-item total">
                <div className="stat-icon total-icon">📱</div>
                <div className="stat-info">
                  <div className="stat-label">Total Reachable</div>
                  <div className="stat-value highlight">
                    {(stats?.users?.withToken || 0) +
                     (stats?.restaurants?.withToken || 0) +
                     (stats?.delivery?.withToken || 0)}
                  </div>
                  <div className="stat-total">devices registered</div>
                </div>
              </div>
            </div>

            {/* Export customers (name + phone) as Excel, scoped by push reachability */}
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eee', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>⬇️ Export customers (Name + Phone):</span>
              <select value={exportFilter} onChange={(e) => setExportFilter(e.target.value)} className="form-control" style={{ width: 'auto', maxWidth: 260 }}>
                <option value="reachable">Reachable — with push token ({stats?.users?.withToken || 0})</option>
                <option value="unreachable">Unreachable — no push token ({Math.max(0, (stats?.users?.total || 0) - (stats?.users?.withToken || 0))})</option>
                <option value="all">All customers ({stats?.users?.total || 0})</option>
              </select>
              <button type="button" className="btn btn-primary" onClick={handleExportCustomers} disabled={exporting}>
                {exporting ? 'Exporting…' : '📥 Download Excel'}
              </button>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>Opens in Excel (.xlsx); phone kept as text.</span>
            </div>
          </div>

          {/* Token maintenance (#3 — keep reach counts fresh) */}
          <div className="notifications-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#666' }}>Token freshness: drop FCM tokens not refreshed in 270 days (FCM&apos;s expiry). Users re-register automatically on next app open.</span>
            <button type="button" className="btn btn-secondary" onClick={handlePruneStale}>🧹 Prune stale tokens</button>
          </div>

          {/* Notification Tabs */}
          <div className="notifications-card">
            <div className="tabs-header">
              <button
                className={`tab-btn ${activeTab === 'broadcast' ? 'active' : ''}`}
                onClick={() => setActiveTab('broadcast')}
              >
                📢 Broadcast Notification
              </button>
              <button
                className={`tab-btn ${activeTab === 'individual' ? 'active' : ''}`}
                onClick={() => setActiveTab('individual')}
              >
                👤 Send to Individual
              </button>
              <button
                className={`tab-btn ${activeTab === 'whatsapp' ? 'active' : ''}`}
                onClick={() => setActiveTab('whatsapp')}
              >
                💬 WhatsApp Broadcast
              </button>
            </div>

            {/* Broadcast Tab */}
            {activeTab === 'broadcast' && (
              <div className="tab-content">
                <h3>Send Broadcast Notification</h3>
                <p className="form-description">
                  Send a push notification to multiple users based on their role and location.
                </p>

                <form onSubmit={handleBroadcastSubmit}>
                  <div className="form-group">
                    <label>Target Audience</label>
                    <div className="audience-selector">
                      {['all', 'users', 'restaurants', 'delivery'].map((audience) => (
                        <label
                          key={audience}
                          className={`audience-option ${broadcastForm.targetAudience === audience ? 'selected' : ''}`}
                        >
                          <input
                            type="radio"
                            name="targetAudience"
                            value={audience}
                            checked={broadcastForm.targetAudience === audience}
                            onChange={(e) => setBroadcastForm({
                              ...broadcastForm,
                              targetAudience: e.target.value
                            })}
                          />
                          <span className="audience-label">{getAudienceLabel(audience)}</span>
                          <span className="audience-count">
                            {audience === 'all'
                              ? (stats?.users?.withToken || 0) + (stats?.restaurants?.withToken || 0) + (stats?.delivery?.withToken || 0)
                              : audience === 'users'
                                ? stats?.users?.withToken || 0
                                : audience === 'restaurants'
                                  ? stats?.restaurants?.withToken || 0
                                  : stats?.delivery?.withToken || 0
                            } users
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="broadcastCity">Filter by Location</label>
                    <select
                      id="broadcastCity"
                      className="form-control"
                      value={broadcastForm.city}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, city: e.target.value })}
                    >
                      <option value="">All Locations</option>
                      {cities.map((c) => (
                        <option key={c._id || c.name} value={c.name}>
                          {c.name}{c.state ? `, ${c.state}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="targeting-summary">
                      {broadcastCount.loading ? (
                        <span>Calculating recipients…</span>
                      ) : (
                        <span>
                          🎯 Targeting <strong>{getAudienceLabel(broadcastForm.targetAudience)}</strong>
                          {broadcastForm.city ? <> in <strong>{broadcastForm.city}</strong></> : ' (all locations)'}:
                          {' '}<strong>{broadcastCount.withToken}</strong> with push enabled
                          {' '}<span className="muted">(of {broadcastCount.total} matching)</span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="broadcastTitle">Notification Title</label>
                    <input
                      type="text"
                      id="broadcastTitle"
                      value={broadcastForm.title}
                      onChange={(e) => setBroadcastForm({
                        ...broadcastForm,
                        title: e.target.value
                      })}
                      placeholder="Enter notification title"
                      className="form-control"
                      maxLength={100}
                      required
                    />
                    <small className="form-help">{broadcastForm.title.length}/100 characters</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="broadcastBody">Notification Body</label>
                    <textarea
                      id="broadcastBody"
                      value={broadcastForm.body}
                      onChange={(e) => setBroadcastForm({
                        ...broadcastForm,
                        body: e.target.value
                      })}
                      placeholder="Enter notification message"
                      className="form-control textarea"
                      rows={4}
                      maxLength={500}
                      required
                    />
                    <small className="form-help">{broadcastForm.body.length}/500 characters</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="broadcastImage">Image URL (optional)</label>
                    <input
                      id="broadcastImage"
                      type="url"
                      value={broadcastForm.imageUrl}
                      onChange={(e) => setBroadcastForm({ ...broadcastForm, imageUrl: e.target.value })}
                      placeholder="https://…/photo.jpg — or upload below"
                      className="form-control"
                    />
                    <div style={{ marginTop: 6 }}>
                      <input type="file" accept="image/*" disabled={uploading === 'broadcast'}
                        onChange={(e) => uploadImage(e.target.files?.[0], 'broadcast')} />
                      {uploading === 'broadcast' && <span style={{ marginLeft: 8, fontSize: 12 }}>Uploading…</span>}
                    </div>
                    <small className="form-help">Paste a public https URL <b>or upload an image</b> to send a rich photo notification (recommended ~2:1, e.g. 1024×512).</small>
                    {(broadcastForm.imageUrl || '').trim() && (
                      <img
                        src={(broadcastForm.imageUrl || '').trim()}
                        alt="preview"
                        style={{ marginTop: 8, maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        onLoad={(e) => { e.target.style.display = 'block'; }}
                      />
                    )}
                  </div>

                  <div className="notification-preview">
                    <h4>Preview</h4>
                    <div className="preview-card">
                      <div className="preview-icon">🔔</div>
                      <div className="preview-content">
                        <div className="preview-title">{broadcastForm.title || 'Notification Title'}</div>
                        <div className="preview-body">{broadcastForm.body || 'Notification message will appear here...'}</div>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-send"
                    disabled={sending || !broadcastForm.title.trim() || !broadcastForm.body.trim()}
                  >
                    {sending
                      ? 'Sending...'
                      : `📢 Send to ${getAudienceLabel(broadcastForm.targetAudience)}${broadcastForm.city ? ` · ${broadcastForm.city}` : ''}`}
                  </button>
                </form>
              </div>
            )}

            {/* Individual Tab */}
            {activeTab === 'individual' && (
              <div className="tab-content">
                <h3>Send to Individual User</h3>
                <p className="form-description">
                  Search and filter users by name, phone, email or location, then pick a recipient.
                </p>

                {/* Recipient picker */}
                <div className="recipient-picker">
                  <div className="recipient-controls">
                    <select
                      className="form-control"
                      value={picker.role}
                      onChange={(e) => setPicker({ ...picker, role: e.target.value })}
                    >
                      <option value="all">All Roles</option>
                      <option value="users">Customers</option>
                      <option value="restaurants">Restaurants</option>
                      <option value="delivery">Delivery Partners</option>
                    </select>

                    <select
                      className="form-control"
                      value={picker.city}
                      onChange={(e) => setPicker({ ...picker, city: e.target.value })}
                    >
                      <option value="">All Locations</option>
                      {cities.map((c) => (
                        <option key={c._id || c.name} value={c.name}>
                          {c.name}{c.state ? `, ${c.state}` : ''}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      className="form-control recipient-search"
                      placeholder="Search name, phone or email…"
                      value={picker.search}
                      onChange={(e) => setPicker({ ...picker, search: e.target.value })}
                    />
                  </div>

                  <div className="recipient-meta">
                    {recipMeta.loading
                      ? 'Searching…'
                      : `${recipMeta.total} match${recipMeta.total === 1 ? '' : 'es'} · ${recipMeta.withTokenCount} with push enabled`}
                  </div>

                  <div className="recipient-list">
                    {recipients.length === 0 && !recipMeta.loading && (
                      <div className="recipient-empty">No users match these filters.</div>
                    )}
                    {recipients.map((u) => {
                      const badge = roleBadge(u.role);
                      const isSel = selectedUser && selectedUser._id === u._id;
                      return (
                        <button
                          type="button"
                          key={u._id}
                          className={`recipient-item ${isSel ? 'selected' : ''}`}
                          onClick={() => setSelectedUser(u)}
                        >
                          <div className="recipient-main">
                            <span className="recipient-name">{u.name || '(no name)'}</span>
                            <span className={`recipient-role ${badge.cls}`}>{badge.label}</span>
                          </div>
                          <div className="recipient-sub">
                            <span>{u.phone || u.email || '—'}</span>
                            {u.city ? <span className="recipient-city">📍 {u.city}</span> : null}
                            <span className={`recipient-token ${u.hasToken ? 'on' : 'off'}`}>
                              {u.hasToken ? 'push on' : 'no push'}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {recipMeta.page < recipMeta.totalPages && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-loadmore"
                      disabled={recipMeta.loading}
                      onClick={() => fetchRecipients(recipMeta.page + 1, true)}
                    >
                      {recipMeta.loading ? 'Loading…' : 'Load more'}
                    </button>
                  )}
                </div>

                <form onSubmit={handleIndividualSubmit}>
                  <div className="form-group">
                    <label>Selected Recipient</label>
                    {selectedUser ? (
                      <div className="selected-recipient">
                        <div>
                          <strong>{selectedUser.name || '(no name)'}</strong>
                          {' '}<span className={`recipient-role ${roleBadge(selectedUser.role).cls}`}>{roleBadge(selectedUser.role).label}</span>
                          <div className="selected-sub">
                            {selectedUser.phone || selectedUser.email || '—'}
                            {selectedUser.city ? ` · 📍 ${selectedUser.city}` : ''}
                            {!selectedUser.hasToken ? ' · ⚠️ no push token' : ''}
                          </div>
                        </div>
                        <button type="button" className="btn btn-text" onClick={() => setSelectedUser(null)}>
                          Clear
                        </button>
                      </div>
                    ) : (
                      <div className="no-recipient">Pick a user from the list above.</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="individualTitle">Notification Title</label>
                    <input
                      type="text"
                      id="individualTitle"
                      value={individualForm.title}
                      onChange={(e) => setIndividualForm({
                        ...individualForm,
                        title: e.target.value
                      })}
                      placeholder="Enter notification title"
                      className="form-control"
                      maxLength={100}
                      required
                    />
                    <small className="form-help">{individualForm.title.length}/100 characters</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="individualBody">Notification Body</label>
                    <textarea
                      id="individualBody"
                      value={individualForm.body}
                      onChange={(e) => setIndividualForm({
                        ...individualForm,
                        body: e.target.value
                      })}
                      placeholder="Enter notification message"
                      className="form-control textarea"
                      rows={4}
                      maxLength={500}
                      required
                    />
                    <small className="form-help">{individualForm.body.length}/500 characters</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="individualImage">Image URL (optional)</label>
                    <input
                      id="individualImage"
                      type="url"
                      value={individualForm.imageUrl}
                      onChange={(e) => setIndividualForm({ ...individualForm, imageUrl: e.target.value })}
                      placeholder="https://…/photo.jpg — or upload below"
                      className="form-control"
                    />
                    <div style={{ marginTop: 6 }}>
                      <input type="file" accept="image/*" disabled={uploading === 'individual'}
                        onChange={(e) => uploadImage(e.target.files?.[0], 'individual')} />
                      {uploading === 'individual' && <span style={{ marginLeft: 8, fontSize: 12 }}>Uploading…</span>}
                    </div>
                    <small className="form-help">Paste a URL <b>or upload an image</b>. Great for testing photo notifications on your own account. Recommend ~2:1 (e.g. 1024×512).</small>
                    {(individualForm.imageUrl || '').trim() && (
                      <img
                        src={(individualForm.imageUrl || '').trim()}
                        alt="preview"
                        style={{ marginTop: 8, maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                        onLoad={(e) => { e.target.style.display = 'block'; }}
                      />
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-send"
                    disabled={sending || !selectedUser || !individualForm.title.trim() || !individualForm.body.trim()}
                  >
                    {sending ? 'Sending...' : '👤 Send to User'}
                  </button>
                </form>
              </div>
            )}

            {/* WhatsApp Broadcast Tab */}
            {activeTab === 'whatsapp' && (
              <div className="tab-content">
                <h3>WhatsApp Broadcast (MSG91)</h3>
                <p className="form-description">
                  Reach users WhatsApp can hit but push can't (no app / no FCM token). Sends a pre-approved WhatsApp template.
                </p>
                {!waCount.configured && (
                  <div className="message error" style={{ marginBottom: 12 }}>
                    ⚠ WhatsApp isn't configured yet. Add <code>MSG91_AUTH_KEY</code> + <code>MSG91_WHATSAPP_NUMBER</code> to the server env (and get a template approved) to go live. You can still set up the message below.
                  </div>
                )}

                <div className="form-group">
                  <label>Audience</label>
                  <div className="audience-selector">
                    {[['no_token', 'No push token (unreachable by push)'], ['has_token', 'Has push token'], ['all', 'All customers']].map(([val, label]) => (
                      <label key={val} className={`audience-option ${whatsappForm.audience === val ? 'selected' : ''}`}>
                        <input type="radio" name="waAudience" value={val} checked={whatsappForm.audience === val} onChange={(e) => setWhatsappForm({ ...whatsappForm, audience: e.target.value })} />
                        <span className="audience-label">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="waCity">Filter by Location</label>
                  <select id="waCity" className="form-control" value={whatsappForm.city} onChange={(e) => setWhatsappForm({ ...whatsappForm, city: e.target.value })}>
                    <option value="">All Locations</option>
                    {cities.map((c) => (<option key={c._id || c.name} value={c.name}>{c.name}{c.state ? `, ${c.state}` : ''}</option>))}
                  </select>
                </div>

                <div className="recipient-meta">
                  {waCount.loading ? 'Counting…' : `${waCount.withPhone} user(s) with a phone in this audience${waCount.total !== waCount.withPhone ? ` (of ${waCount.total} matched)` : ''}`}
                </div>

                <div className="form-group">
                  <label htmlFor="waTemplate">Approved Template Name</label>
                  <input id="waTemplate" type="text" className="form-control" placeholder="e.g. foodwinc_comeback" value={whatsappForm.templateName} onChange={(e) => setWhatsappForm({ ...whatsappForm, templateName: e.target.value })} />
                  <small className="form-help">The exact name of your Meta/MSG91-approved WhatsApp template.</small>
                </div>

                <div className="form-group">
                  <label htmlFor="waLang">Template Language Code</label>
                  <input id="waLang" type="text" className="form-control" placeholder="en" value={whatsappForm.languageCode} onChange={(e) => setWhatsappForm({ ...whatsappForm, languageCode: e.target.value })} />
                </div>

                <div className="form-group">
                  <label>
                    <input type="checkbox" checked={whatsappForm.useName} onChange={(e) => setWhatsappForm({ ...whatsappForm, useName: e.target.checked })} />
                    {' '}Personalize — use each user&apos;s name as the first body variable ({'{{1}}'})
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="waVars">Other body variables (optional)</label>
                  <input id="waVars" type="text" className="form-control" placeholder="value for {{2}} | value for {{3}} …" value={whatsappForm.bodyVars} onChange={(e) => setWhatsappForm({ ...whatsappForm, bodyVars: e.target.value })} />
                  <small className="form-help">Separate values with &quot; | &quot;. They fill the template&apos;s body placeholders in order (after the name, if enabled).</small>
                </div>

                <div className="form-group">
                  <label htmlFor="waTest">Test phone (send only to this one number)</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input id="waTest" type="text" className="form-control" placeholder="+9193XXXXXXXX" value={whatsappForm.testPhone} onChange={(e) => setWhatsappForm({ ...whatsappForm, testPhone: e.target.value })} />
                    <button type="button" className="btn btn-secondary" disabled={waSending || !whatsappForm.templateName.trim()} onClick={() => handleWhatsAppSend(true)}>
                      {waSending ? 'Sending…' : 'Send Test'}
                    </button>
                  </div>
                </div>

                <button type="button" className="btn btn-primary btn-send" disabled={waSending || !whatsappForm.templateName.trim() || waCount.withPhone === 0} onClick={() => handleWhatsAppSend(false)}>
                  {waSending ? 'Sending…' : `💬 Send WhatsApp to ${waCount.withPhone} user(s)`}
                </button>
              </div>
            )}

            {message.text && (
              <div className={`message ${message.type}`}>
                {message.text}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h3>ℹ️ Push Notification Information</h3>
            <ul>
              <li><strong>FCM Token:</strong> Users must have the app installed and notifications enabled to receive push notifications.</li>
              <li><strong>Location Filter:</strong> Targets users by city — customers by their saved address/location, delivery partners by work city, and restaurants by their registered city.</li>
              <li><strong>Target Audiences:</strong>
                <ul>
                  <li><strong>All Users:</strong> Sends to customers, restaurants, and delivery partners</li>
                  <li><strong>Customers:</strong> Regular app users who order food</li>
                  <li><strong>Restaurants:</strong> Restaurant owners and managers (web and mobile)</li>
                  <li><strong>Delivery Partners:</strong> Delivery personnel with the delivery app</li>
                </ul>
              </li>
              <li><strong>Delivery Status:</strong> Not all notifications may be delivered if devices are offline or tokens are expired.</li>
              <li><strong>Character Limits:</strong> Title: 100 characters, Body: 500 characters for best display across devices.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
